<script>
import lazyLoadComponent from '@router/lazyload-component'

import {
  genderFilter,
  orderStatusMap,
  orderStatusFilter,
  orderPaymentMethodMap,
  orderPaymentMethodFilter,
  orderSourceMap,
  orderSourceFilter,
  productTypeList,
} from '@utils/filter-value-to-text'
import formatDate, { formatRequestData } from '@utils/format-date'

import pageMixin from '@src/mixins/pagination'
import { PortalTarget } from 'portal-vue'

export default {
  page: {
    title: '訂單查詢',
    meta: [{ name: 'description', content: 'Order List' }],
  },
  components: {
    PortalTarget,
    BaseStateLabel: () =>
      lazyLoadComponent(import('@components/BaseStateLabel')),
    OrderDetail: () =>
      lazyLoadComponent(import('@components/PrepaidOrderDetail')),
  },
  filters: {
    genderFilter,
    orderStatusFilter,
    orderPaymentMethodFilter,
    orderSourceFilter,
    formatDate,
  },
  mixins: [pageMixin],
  data() {
    return {
      drawerVisible: false,
      drawerOrderData: '',
      filterData: {
        status: '',
        source: '',
        startDate: '',
        endDate: '',
        paymentMethod: 'All',
        productType: '',
      },
      statusList: Object.freeze(orderStatusMap),
      sourceList: Object.freeze(orderSourceMap),
      paymentMethodList: Object.freeze(orderPaymentMethodMap),
      productTypeList: Object.freeze(productTypeList),
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一週',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近一個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近三個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            },
          },
        ],
      },
      loadedOrders: [],
      queryProductName: '',
      tryingToGetOrder: false,
    }
  },
  computed: {
    filterDateRange: {
      get() {
        return [this.filterData.startDate, this.filterData.endDate]
      },
      set(value) {
        const [start, end] = value
        this.filterData.startDate = start
        this.filterData.endDate = end
      },
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToFetchList',
      immediate: true,
    },
  },
  methods: {
    tableRowClassName({ row }) {
      const rowStatus = row.status
      const usedStatus = row.groupSize / row.usedSize
      if (rowStatus === 0) return 'order--canceled'
      if (usedStatus === 1) return 'order--used'
    },
    handleDrawerClosed() {
      this.$refs.orderDetail.mode = 'view'
    },
    async tryToGetOrder(orderId) {
      this.tryingToGetOrder = true
      this.drawerOrderData = await this.$store.dispatch(
        'prepaid/fetchOrderSingle',
        orderId
      )
      this.tryingToGetOrder = false
      this.drawerVisible = true
    },
    async tryToFetchList() {
      const routeQuery = this.$route.query
      const queryData = Object.assign(routeQuery, this.pageQuery)

      const { startDate, endDate } = this.filterData

      const { items, productName, total } = await this.$store.dispatch(
        'prepaid/fetchOrders',
        Object.assign(
          this.filterData,
          {
            startDate: formatRequestData(startDate),
            endDate: formatRequestData(endDate),
          },
          queryData
        )
      )

      this.loadedOrders = items
      this.queryProductName = productName
      this.$nextTick(() => {
        this.$refs.pagination.updateTotal(total)
      })
    },
    clearSearchQuery() {
      this.$router.push(this.$route.path)
    },
  },
}
</script>

<template>
  <div>
    <BaseTableSearch v-slot="{ fields }" :default-value="filterData" @reset="tryToFetchList">
      <el-form-item prop="status">
        <el-select v-model="fields.status" placeholder="選擇訂單狀態" @change="tryToFetchList">
          <el-option value>所有訂單狀態</el-option>
          <el-option
            v-for="(status, statusValue) in statusList"
            :key="statusValue"
            :label="status.text"
            :value="statusValue"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="paymentMethod">
        <el-select v-model="fields.paymentMethod" placeholder="選擇付款方式" @change="tryToFetchList">
          <el-option label="所有付款方式" value="All"></el-option>
          <el-option
            v-for="(label, value) in paymentMethodList"
            :key="value"
            :label="label"
            :value="value"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="source">
        <el-select v-model="fields.source" placeholder="選擇訂單來源" @change="tryToFetchList">
          <el-option label="所有訂單來源" value></el-option>
          <el-option
            v-for="(label, value) in sourceList"
            :key="value"
            :label="label"
            :value="value"
          ></el-option>
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-date-picker
          v-model="filterDateRange"
          type="daterange"
          align="right"
          unlink-panels
          range-separator="-"
          start-placeholder="起始日期"
          end-placeholder="結束日期"
          :picker-options="pickerOptions"
          @change="tryToFetchList"
        ></el-date-picker>
      </el-form-item>
      <el-form-item prop="productType">
        <el-select v-model="fields.productType" placeholder="適用項目類型" @change="tryToFetchList">
          <el-option
            v-for="(type, typeValue) in productTypeList"
            :key="typeValue"
            :label="type.name"
            :value="typeValue"
          ></el-option>
        </el-select>
      </el-form-item>
    </BaseTableSearch>
    <el-table
      v-loading.fullscreen.lock="tryingToGetOrder"
      element-loading-text="取得訂單資訊中"
      :data="loadedOrders"
      :default-sort="{ prop: 'created', order: 'descending' }"
      :row-class-name="tableRowClassName"
    >
      <el-table-column v-slot="{ row }" label="#訂單編號" width="220" sortable>
        <div>{{ `#${row.orderNo}` }}</div>
        <div>{{ row.created | formatDate('dateTime') }}</div>
      </el-table-column>
      <el-table-column v-slot="{ row }" label="狀態與來源" width="120" sortable>
        <BaseStateLabel
          :state="row.status | orderStatusFilter('color') "
          text
        >{{ row.status | orderStatusFilter('text') }}</BaseStateLabel>
        <br />
        <BaseStateLabel state="info" text>{{ row.source | orderSourceFilter }}</BaseStateLabel>
      </el-table-column>
      <el-table-column
        v-slot="{ row }"
        label="付款方式"
        width="100"
        sortable
      >{{ row.paymentMethod | orderPaymentMethodFilter }}</el-table-column>
      <el-table-column v-slot="{ row }" label="客戶資訊">
        <div>{{ row.fullName }} {{ row.gender | genderFilter }}</div>
        <div>{{ `${row.phoneCode} ${row.phoneNumber}` }}</div>
      </el-table-column>
      <el-table-column v-slot="{ row }" label="預付方案名稱">
        <div>
          <b>{{ row.prepaidTitle }}</b>
        </div>
        <div>{{ row.date | formatDate }} {{ row.time }}</div>
      </el-table-column>
      <el-table-column v-slot="{ row }">
        <el-button @click="tryToGetOrder(row.id)">查看詳情</el-button>
      </el-table-column>
    </el-table>
    <BasePagination ref="pagination" :current-page.sync="currentPage" />
    <el-drawer :visible.sync="drawerVisible" append-to-body size="50%" @closed="handleDrawerClosed">
      <!-- header -->
      <PortalTarget name="drawer-header" slot="title"></PortalTarget>
      <OrderDetail
        ref="orderDetail"
        :default-value.sync="drawerOrderData"
        @update="tryToFetchList"
      />
    </el-drawer>
  </div>
</template>